import React, { useEffect, useRef, useState } from "react";
import { Heart, Trophy } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { REACTIONS, REACTION_BY_KIND } from "@/components/reactions";

/**
 * Renders:
 *   1. Existing aggregate count pills (one per reaction kind that has > 0 votes).
 *   2. A trigger Heart button — clicking opens a row of all 6 reaction emojis.
 *   3. A small "X people reacted" trophy summary above the row when total > 0.
 *
 * Props:
 *   commentId, isOwn (bool), reactions ({trophy:n,...}), myReactions ([kind]),
 *   onChange(updated)  - parent updates its local state with the API response.
 */
export default function ReactionBar({ commentId, isOwn, reactions, myReactions, onChange }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [busyKind, setBusyKind] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const total = Object.values(reactions || {}).reduce((a, b) => a + Number(b || 0), 0);

  const sendReaction = async (kind) => {
    if (!user) {
      toast.error("Sign in to react"); return;
    }
    if (isOwn) {
      toast.error("You can't react to your own comment"); return;
    }
    setBusyKind(kind);
    try {
      const { data } = await api.post(`/comments/${commentId}/react`, { kind });
      onChange?.({
        reactions: data.reactions,
        my_reactions: data.my_reactions,
        reactions_total: data.reactions_total,
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reaction failed");
    } finally {
      setBusyKind(null);
      setOpen(false);
    }
  };

  // Inline count pills (only kinds with count > 0)
  const activeKinds = REACTIONS.filter((r) => (reactions?.[r.kind] || 0) > 0);

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {activeKinds.map((r) => {
        const mine = (myReactions || []).includes(r.kind);
        return (
          <button
            key={r.kind}
            type="button"
            onClick={() => sendReaction(r.kind)}
            disabled={isOwn || busyKind === r.kind}
            data-testid={`reaction-pill-${r.kind}-${commentId}`}
            title={`${r.label} · ${reactions[r.kind]}`}
            className={`group inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-xs transition-colors
              ${mine
                ? "bg-[var(--tt-primary)]/15 border-[var(--tt-primary)]/50 text-white"
                : "bg-white/[0.04] border-[var(--tt-border)] text-[var(--tt-text-muted)] hover:text-white hover:border-white/30"}
              ${isOwn ? "opacity-70 cursor-default" : "cursor-pointer"}`}
          >
            <img src={r.url} alt="" className="w-3.5 h-3.5" />
            <span className="font-mono-tt text-[11px] leading-none">{reactions[r.kind]}</span>
          </button>
        );
      })}
      {total > 0 && !isOwn && (
        <span className="hidden sm:inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-[var(--tt-text-faint)] ml-1">
          <Trophy className="w-3 h-3 text-[var(--tt-yellow)]" />
          {total} reacted
        </span>
      )}

      {/* Trigger + popover */}
      {!isOwn && user && (
        <div className="relative" ref={wrapRef}>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            data-testid={`reaction-open-${commentId}`}
            title="React to this comment"
            className="inline-flex items-center justify-center w-6 h-6 rounded-full text-[var(--tt-text-muted)] hover:text-[var(--tt-primary)] hover:bg-white/[0.04]"
          >
            <Heart className="w-3.5 h-3.5" />
          </button>
          {open && (
            <div
              data-testid={`reaction-tray-${commentId}`}
              className="absolute z-30 bottom-full mb-2 left-1/2 -translate-x-1/2 flex items-center gap-0.5 bg-[#0c0c0c] border border-[var(--tt-border)] rounded-full px-2 py-1.5 shadow-2xl"
            >
              {REACTIONS.map((r) => {
                const mine = (myReactions || []).includes(r.kind);
                return (
                  <button
                    key={r.kind}
                    type="button"
                    onClick={() => sendReaction(r.kind)}
                    disabled={busyKind === r.kind}
                    title={r.label}
                    data-testid={`reaction-btn-${r.kind}-${commentId}`}
                    className={`relative w-8 h-8 flex items-center justify-center rounded-full transition-transform hover:scale-125 active:scale-95
                      ${mine ? "bg-[var(--tt-primary)]/20 ring-1 ring-[var(--tt-primary)]/60" : "hover:bg-white/[0.06]"}`}
                  >
                    <img src={r.url} alt="" className="w-5 h-5" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
